import { ComponentType } from '@angular/cdk/overlay';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OverlayService } from '@core';
import { LogoutComponent, SessionExpiredComponent } from '@logout';
import { OVERLAY_CONFIG_BY_TYPE, SESSION_TIMEOUT, TimeoutTypeEnum } from '@models';
import { BehaviorSubject, fromEvent, merge, Observable, timer } from 'rxjs';
import { debounceTime, filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { StorageService } from './storage.service';

const COMPONENT_BY_TYPE: Record<TimeoutTypeEnum, ComponentType<unknown>> = {
  [TimeoutTypeEnum.IDLE_SESSION]: LogoutComponent,
  [TimeoutTypeEnum.LOGOUT]: SessionExpiredComponent,
};

const EXPIRES_REASON = {
  IDLE: 'idle',
  TOKEN: 'token',
} as const;

const REASONS = Object.values(EXPIRES_REASON);

const DOCUMENT_ACTIVITY_EVENTS = [
  'mousemove',
  'click',
  'mousedown',
  'keypress',
  'DOMMouseScroll',
  'mousewheel',
  'touchmove',
  'MSPointerMove',
];

const WINDOW_ACTIVITY_EVENTS = ['mousemove', 'resize'];

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private readonly timeOutMilliSeconds: number = SESSION_TIMEOUT;
  private readonly timeoutType = TimeoutTypeEnum.LOGOUT;

  private destroyRef = inject(DestroyRef);
  private lStorage = inject(StorageService);
  private authService = inject(AuthService);
  private overlayService = inject(OverlayService);

  private readonly userActivity$ = merge(
    ...DOCUMENT_ACTIVITY_EVENTS.map((event) => fromEvent(document, event)),
    ...WINDOW_ACTIVITY_EVENTS.map((event) => fromEvent(window, event)),
  ).pipe(debounceTime(500), shareReplay(1));

  private readonly resetIdleTimerSubject = new BehaviorSubject<void>(undefined);

  public setupSessionTimeoutFeature(): void {
    if (this.isSessionExpired()) {
      this.onExpired();
      return;
    }

    merge(
      // Idle logout (7 min, SESSION_TIMEOUT) is disabled: the casino session lasts the whole event day
      // and ends with the event (RCP-238 DoD8). Only token expiry ends the session now.
      // this.dropIdleTimerOnUserActivity(),
      // this.getIdleTimeoutStream().pipe(map(() => EXPIRES_REASON.IDLE)),
      this.authService.tokenExpired$.pipe(map(() => EXPIRES_REASON.TOKEN)),
    )
      .pipe(
        filter((reason) => REASONS.includes(reason)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (!this.isSessionExpired()) {
          this.lStorage.isSessionExpired = true;
          this.onExpired();
        }
      });
  }

  public clearSessionExpiration() {
    this.lStorage.isSessionExpired = false;
  }

  private dropIdleTimerOnUserActivity(): Observable<never> {
    return this.userActivity$.pipe(
      map(() => {
        this.resetIdleTimerSubject.next();
        return undefined as never;
      }),
    );
  }

  private getIdleTimeoutStream(): Observable<unknown> {
    return this.resetIdleTimerSubject.pipe(switchMap(() => timer(this.timeOutMilliSeconds)));
  }

  private onExpired(): void {
    const overlayConfig = OVERLAY_CONFIG_BY_TYPE[this.timeoutType];
    const component = COMPONENT_BY_TYPE[this.timeoutType];
    if (this.timeoutType === TimeoutTypeEnum.LOGOUT) {
      this.authService.logout();
    }
    this.overlayService.open(component, undefined, overlayConfig);
  }

  isSessionExpired(): boolean {
    return this.lStorage.isSessionExpired;
  }
}
